/**
 * Moteur fiscal français complet (Barème IR 2024 - Revenus 2023 / Barème actualisé)
 */

// Barème progressif IR 2024
export const IR_BAREME_2024 = [
  { limit: 11294, taux: 0 },
  { limit: 28797, taux: 0.11 },
  { limit: 82341, taux: 0.30 },
  { limit: 177106, taux: 0.41 },
  { limit: Infinity, taux: 0.45 }
];

// Barème IFI 2024
export const IFI_BAREME_2024 = [
  { limit: 800000, taux: 0 },
  { limit: 1300000, taux: 0.005 },
  { limit: 2570000, taux: 0.007 },
  { limit: 5000000, taux: 0.01 },
  { limit: 10000000, taux: 0.0125 },
  { limit: Infinity, taux: 0.015 }
];

/**
 * Calcul précis des parts de quotient familial
 */
export function calculateFiscalParts(statut, enfantsACharge = 0, enfantsGardeAlternee = 0, parentIsole = false, personneInvalide = false) {
  let baseParts = 1;
  if (statut === 'marie' || statut === 'pacse') {
    baseParts = 2;
  } else if (statut === 'veuf' && (enfantsACharge > 0 || enfantsGardeAlternee > 0)) {
    baseParts = 2; // Demi-part veuf avec charge d'enfant
  }

  // Enfants à charge exclusive
  let enfantsParts = 0;
  if (enfantsACharge === 1) enfantsParts = 0.5;
  else if (enfantsACharge === 2) enfantsParts = 1.0;
  else if (enfantsACharge > 2) enfantsParts = 1.0 + (enfantsACharge - 2);

  // Enfants en garde alternée
  let alterneeParts = 0;
  if (enfantsGardeAlternee === 1) alterneeParts = 0.25;
  else if (enfantsGardeAlternee === 2) alterneeParts = 0.5;
  else if (enfantsGardeAlternee > 2) alterneeParts = 0.5 + ((enfantsGardeAlternee - 2) * 0.5);

  let bonusParts = 0;
  if (parentIsole && (enfantsACharge > 0 || enfantsGardeAlternee > 0)) {
    bonusParts += 0.5; // Demi-part parent isolé
  }
  if (personneInvalide) {
    bonusParts += 0.5; // Demi-part invalidité
  }

  return baseParts + enfantsParts + alterneeParts + bonusParts;
}

/**
 * Calcul complet de l'Impôt sur le Revenu (IR)
 */
export function calculateTaxProfile(fiscalData) {
  const {
    statut = 'celibataire',
    enfants = 0,
    enfantsGardeAlternee = 0,
    parentIsole = false,
    personneInvalide = false,

    // Traitements & Salaires
    salaireDeclarant1 = 0,
    salaireDeclarant2 = 0,
    typeFrais = 'forfait', // 'forfait' (10%) ou 'reel'
    fraisReelsDeclarant1 = 0,
    fraisReelsDeclarant2 = 0,

    // Revenus Fonciers
    regimeFoncier = 'reel', // 'micro' (abattement 30%) ou 'reel'
    loyersBruts = 0,
    chargesDeductiblesFoncier = 0,
    interetsEmpruntFoncier = 0,
    travauxFoncier = 0,

    // RCM & Dividendes
    dividendesBruts = 0,
    optionDividendes = 'pfu', // 'pfu' (30%) ou 'bareme' (abattement 40%)

    // Autres revenus
    bicBncPro = 0,
    pensionsRetraite = 0,

    // Épargne Retraite (PER)
    versementPer = 0,

    // Réductions et Crédits
    enfantsCollege = 0,
    enfantsLycee = 0,
    enfantsSuperieur = 0,
    emploiDomicile = 0,
    donsColuche = 0,
    donsGeneral = 0,
    investissementsDefisc = 0, // Pinel, Girardin, etc.
  } = fiscalData;

  // 1. Calcul des parts fiscales
  const parts = calculateFiscalParts(statut, enfants, enfantsGardeAlternee, parentIsole, personneInvalide);

  // 2. Traitements & Salaires avec abattement 10% (min 495€, max 14171€ par déclarant)
  const calcSalaires = (sal, fraisReels) => {
    if (sal <= 0) return 0;
    if (typeFrais === 'reel') {
      return Math.max(0, sal - fraisReels);
    }
    const abattement = Math.min(14171, Math.max(495, sal * 0.10));
    return Math.max(0, sal - abattement);
  };

  const salairesNetImposable = calcSalaires(salaireDeclarant1, fraisReelsDeclarant1) + 
    (statut === 'marie' || statut === 'pacse' ? calcSalaires(salaireDeclarant2, fraisReelsDeclarant2) : 0);

  // 3. Revenus Fonciers
  let revenuFoncierImposable = 0;
  let deficitFoncierImputable = 0;

  if (regimeFoncier === 'micro' && loyersBruts <= 15000) {
    revenuFoncierImposable = loyersBruts * 0.70; // Abattement de 30%
  } else {
    const totalCharges = chargesDeductiblesFoncier + interetsEmpruntFoncier + travauxFoncier;
    const resultatFoncier = loyersBruts - totalCharges;
    if (resultatFoncier >= 0) {
      revenuFoncierImposable = resultatFoncier;
    } else {
      // Déficit foncier : imputable jusqu'à 10 700 € sur le revenu global hors intérêts
      const horsInterets = Math.max(0, chargesDeductiblesFoncier + travauxFoncier - loyersBruts);
      deficitFoncierImputable = Math.min(10700, horsInterets);
    }
  }

  // 4. Pensions de retraite (abattement 10% plafonné à ~4321€)
  const abattementPensions = Math.min(4321, Math.max(448, pensionsRetraite * 0.10));
  const pensionsNetImposable = Math.max(0, pensionsRetraite - abattementPensions);

  // 5. Revenu Brut Global avant RCM et PER
  let revenuBrutGlobal = salairesNetImposable + revenuFoncierImposable - deficitFoncierImputable + bicBncPro + pensionsNetImposable;
  revenuBrutGlobal = Math.max(0, revenuBrutGlobal);

  // 6. Déduction PER (Plafond 10% des revenus professionnels dans la limite de 8 PASS ~ 35 194 €)
  const revenusPro = salaireDeclarant1 + salaireDeclarant2 + bicBncPro;
  const plafondPerTheorique = Math.min(35194, Math.max(4399, revenusPro * 0.10));
  const deductionPerEffective = Math.min(plafondPerTheorique, versementPer);

  // 7. Dividendes & Comparateur PFU vs Barème
  // Option Barème : Abattement de 40% sur dividendes bruts + déduction fraction CSG 6.8%
  let dividendeImposableBareme = 0;
  let csgDeductibleDividendes = 0;
  if (optionDividendes === 'bareme' && dividendesBruts > 0) {
    dividendeImposableBareme = dividendesBruts * 0.60;
    csgDeductibleDividendes = dividendesBruts * 0.068;
  }

  // Revenu Net Imposable (assiette du barème IR)
  const baseImposable = Math.max(0, revenuBrutGlobal - deductionPerEffective + dividendeImposableBareme - csgDeductibleDividendes);
  const quotientFamilial = baseImposable / parts;

  // 8. Calcul de l'IR Brut selon barème progressif
  let impotTranches = 0;
  let prevLimit = 0;
  let tmi = 0;

  for (const tranche of IR_BAREME_2024) {
    if (quotientFamilial > prevLimit) {
      const taxable = Math.min(quotientFamilial, tranche.limit) - prevLimit;
      impotTranches += taxable * tranche.taux;
      if (tranche.taux > 0) tmi = tranche.taux * 100;
    } else {
      break;
    }
    prevLimit = tranche.limit;
  }

  const impotBrut = Math.round(impotTranches * parts);

  // 9. Décote pour faibles revenus (2024 : seuil célibataire 1929€, couple 3191€)
  let decote = 0;
  const seuilDecote = (statut === 'marie' || statut === 'pacse') ? 3191 : 1929;
  const baseDecote = (statut === 'marie' || statut === 'pacse') ? 1444 : 873;
  if (impotBrut > 0 && impotBrut < seuilDecote) {
    decote = Math.max(0, baseDecote - (impotBrut * 0.4525));
  }

  const impotApresDecote = Math.max(0, Math.round(impotBrut - decote));

  // 10. Réductions et crédits d'impôt
  const reductionScolarite = (enfantsCollege * 61) + (enfantsLycee * 153) + (enfantsSuperieur * 183);
  const creditEmploiDomicile = Math.min(6000, emploiDomicile * 0.50); // Plafond standard 12k€ de dépenses = 6k€ crédit
  const reductionDonsColuche = Math.min(1000, donsColuche) * 0.75;
  const reductionDonsGeneral = donsGeneral * 0.66;
  const totalDons = reductionDonsColuche + reductionDonsGeneral;

  // Plafonnement des niches fiscales (10 000 €)
  const totalNichesFiscales = Math.min(10000, creditEmploiDomicile + investissementsDefisc);
  const totalReductionsCredits = reductionScolarite + totalDons + totalNichesFiscales;

  // Impôt net sur le revenu
  const impotNetIR = Math.max(0, Math.round(impotApresDecote - totalReductionsCredits));

  // 11. Imposition des Dividendes (PFU ou Barème)
  let impotDividendes = 0;
  let prelevementsSociauxDividendes = 0;

  if (dividendesBruts > 0) {
    if (optionDividendes === 'pfu') {
      impotDividendes = Math.round(dividendesBruts * 0.128); // 12.8% IR
      prelevementsSociauxDividendes = Math.round(dividendesBruts * 0.172); // 17.2% PS
    } else {
      // Option barème : déjà inclus dans l'IR progressif, restent les PS à 17.2%
      prelevementsSociauxDividendes = Math.round(dividendesBruts * 0.172);
    }
  }

  // Prélèvements sociaux sur revenus fonciers nets (17.2%)
  const prelevementsSociauxFoncier = Math.round(Math.max(0, revenuFoncierImposable) * 0.172);

  // Total impôt final payé par le contribuable (IR + PFU/PS foncier et dividendes)
  const totalImpotGlobal = impotNetIR + impotDividendes + prelevementsSociauxDividendes + prelevementsSociauxFoncier;

  // 12. Gain fiscal immédiat du PER (Versement × TMI)
  const gainFiscalPer = Math.round(deductionPerEffective * (tmi / 100));
  const effortReelPer = versementPer - gainFiscalPer;

  // 13. Comparateur PFU vs Barème pour l'arbitrage
  // Calcul théorique du coût en PFU vs Barème
  const coutPFU = Math.round(dividendesBruts * 0.30);
  const irBaremeSeul = Math.round((dividendesBruts * 0.60) * (tmi / 100));
  const psBaremeSeul = Math.round(dividendesBruts * 0.172);
  const csgGain = Math.round((dividendesBruts * 0.068) * (tmi / 100));
  const coutBareme = Math.max(0, irBaremeSeul + psBaremeSeul - csgGain);

  const recommandationRCM = coutPFU <= coutBareme ? 'PFU' : 'BAREME';
  const economieRCM = Math.abs(coutPFU - coutBareme);

  return {
    parts,
    revenusPro,
    salairesNetImposable,
    revenuFoncierImposable,
    deficitFoncierImputable,
    pensionsNetImposable,
    revenuBrutGlobal,
    plafondPerTheorique,
    deductionPerEffective,
    gainFiscalPer,
    effortReelPer,
    baseImposable,
    quotientFamilial: Math.round(quotientFamilial),
    tmi,
    impotBrut,
    decote,
    impotApresDecote,
    reductionScolarite,
    totalDons,
    totalNichesFiscales,
    totalReductionsCredits,
    impotNetIR,
    impotDividendes,
    prelevementsSociauxDividendes,
    prelevementsSociauxFoncier,
    totalImpotGlobal,
    // Comparateur RCM
    coutPFU,
    coutBareme,
    recommandationRCM,
    economieRCM,
    tauxMoyenImposition: baseImposable > 0 ? Math.round((impotNetIR / baseImposable) * 1000) / 10 : 0
  };
}

/**
 * Calculateur IFI (Impôt sur la Fortune Immobilière)
 */
export function calculateIfi(residencePrincipale = 0, locatifEtSecondaire = 0, scpiOpci = 0, dettesImmo = 0) {
  // Abattement légal de 30% sur la résidence principale
  const rpTaxable = residencePrincipale * 0.70;
  const actifImmoBrutTaxable = rpTaxable + locatifEtSecondaire + scpiOpci;
  const actifImmoNetTaxable = Math.max(0, actifImmoBrutTaxable - dettesImmo);

  // Seuil d'assujettissement : 1 300 000 €
  if (actifImmoNetTaxable < 1300000) {
    return {
      assujetti: false,
      actifImmoBrutTaxable: Math.round(actifImmoBrutTaxable),
      actifImmoNetTaxable: Math.round(actifImmoNetTaxable),
      ifiBrut: 0,
      decoteIfi: 0,
      ifiNet: 0
    };
  }

  // Calcul barème IFI
  let impot = 0;
  let prevLimit = 0;
  for (const tranche of IFI_BAREME_2024) {
    if (actifImmoNetTaxable > prevLimit) {
      const taxable = Math.min(actifImmoNetTaxable, tranche.limit) - prevLimit;
      impot += taxable * tranche.taux;
    } else {
      break;
    }
    prevLimit = tranche.limit;
  }

  // Décote IFI si patrimoine entre 1 300 000 € et 1 400 000 €
  let decote = 0;
  if (actifImmoNetTaxable >= 1300000 && actifImmoNetTaxable <= 1400000) {
    decote = 17500 - (0.0125 * actifImmoNetTaxable);
  }

  const ifiNet = Math.max(0, Math.round(impot - decote));

  return {
    assujetti: true,
    actifImmoBrutTaxable: Math.round(actifImmoBrutTaxable),
    actifImmoNetTaxable: Math.round(actifImmoNetTaxable),
    ifiBrut: Math.round(impot),
    decoteIfi: Math.round(decote),
    ifiNet
  };
}

